import {
  APP_ACP,
  APP_CAREPROVIDER,
  APP_CLINIC,
  APP_PROVIDER_SEARCH,
} from "core/consts";
import Config, {
  ENV_DEMO,
  ENV_DEVELOPMENT,
  ENV_PREPROD,
  ENV_PRODUCTION,
  ENV_STAGING,
} from "core/model/config";
import { AppType, CyFeatureFlagKey, CyFeatureFlags } from "core/types";

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    Cypress: unknown;
    flags?: Partial<CyFeatureFlags>;
    IN_STORYBOOK?: boolean;
  }
}

function cy(id: CyFeatureFlagKey, value: boolean): boolean {
  if (window.Cypress && window.flags?.[id] != null) {
    return !!window.flags[id];
  }
  return value;
}

export const isCI = (() => {
  if (window) return Boolean(window.Cypress);
  return false;
})();

export const isTest = process.env.NODE_ENV === "test" || window.IN_STORYBOOK;

export const isStoryshots = /StoryShotsVisualTesting/.test(navigator.userAgent);

// check if app is running embedded in an iframe, e.g. Korian integration
// don't rely on useQueryParam to work outside router and not polute global search params
export const isAppEmbedded = () =>
  typeof location !== "undefined" && location.search.includes("embedded");

export const isDev = process.env.ENV === ENV_DEVELOPMENT;

export const isPreprod = process.env.ENV === ENV_PREPROD;

export const isDeveloperMode =
  !isCI && (isDev || process.env.ENV === ENV_STAGING);

export const activateEnvSwitch = isDeveloperMode;

export const isProd =
  Config.environment === ENV_PRODUCTION || Config.environment === ENV_DEMO;

export const isDemo = Config.environment === ENV_DEMO;

export const isStaging = !isProd && Config.environment !== ENV_PREPROD;

export const flagPrivateKey50K = Config.environment !== ENV_PRODUCTION;

export const activateBeamer = () =>
  !isAppEmbedded() &&
  Config.environment !== ENV_DEVELOPMENT &&
  Config.environment !== ENV_STAGING;

export const activateLoggingTracking =
  Config.environment !== ENV_DEVELOPMENT && Config.environment !== ENV_STAGING;

/*
  SenderDashboard
*/

export const activateSelectableProviders = () => {
  return cy("activateSelectableProviders", false);
};

export const activatePWA = !isProd;
export const activateBrowserNotification = !isProd;

export const activateWebSocket = (app: AppType) =>
  cy(
    "activateWebSocket",
    !isCI &&
      (
        [APP_CAREPROVIDER, APP_CLINIC, APP_PROVIDER_SEARCH] as AppType[]
      ).includes(app),
  );

export const activateRealUserMonitoring = isPreprod || isProd;

export const activateSeald = (app: number | null) => app !== APP_ACP;
export const activateSealdGroups = (app: number | null) => activateSeald(app);
export const activateSealdSessionEncryption = (app: number | null) =>
  activateSeald(app) && activateSealdGroups(app);

export const activateEligibilityFiles = true;
export const activateDTAAttachments = !isProd;

export const activateReactHookForm = () => {
  const params = new URLSearchParams(window.location.search);
  const isReactHookForm =
    (!!params.get("activateReactHookForm") ||
      !!(window as any).__ACTIVATE_REACT_HOOK_FORM__) &&
    !isProd;
  return cy("activateReactHookForm", isReactHookForm && !isCI);
};

// Predict New Changes DEV-19107
export const activatePredictNewChanges = !isProd;
export const activateVersionRefreshBanner = !isProd;

/** Summit promo banner in sender app. Set to false to remove after the summit. */
export const activateSummitBanner = true;

export const activatePrintE2ETesting = () => isCI;

export const activateSalesforceChat = () => {
  return cy("activateSalesforceChat", true);
};

export const activateUpdateCareproviderProductFeatures = !isProd;

export const activateCopyMessageHistory = !isProd;

export const activateSolutionToServiceMigration = !isProd && !isCI;

export const activatePsychCare = isStaging || isCI;

export const activatePsychCareReceiver = !isProd;

export const activateFileUploadRedesign = true;

export const activateClinicalDashboard = !isProd;

/** Use FE step config for assessment whitelist fields (no BE fetch). Toggle to switch BE vs FE. */
export const useFeAssessmentFields = !isProd;

export const activateFileDeleteModalRedesign = !isProd;

export const activateEUSCMaintenanceBanner = !isCI;
